import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BarChart3, ClipboardCheck, BookOpen, TrendingUp, Shield, Radio, 
  ShieldAlert, Target, History, Bot, Play, Menu, X, ChevronLeft,
  ChevronRight, Layers, Activity, LogOut, User, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Close mobile sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);
  
  const navGroups = [
    {
      label: 'Main',
      items: [
        { path: '/paper-trading', label: 'Paper Trading', icon: Play, color: 'text-success' },
        { path: '/signals', label: 'Signal Scanner', icon: Radio, color: 'text-success' },
        { path: '/journal', label: 'Trade Journal', icon: BookOpen, color: 'text-primary' },
        { path: '/account', label: 'Automation Preferences', icon: Bot, color: 'text-primary' }
      ]
    },
    {
      label: 'Plan & Validate',
      items: [
        { path: '/', label: 'Market Conditions', icon: TrendingUp, color: 'text-muted-foreground' },
        { path: '/validator', label: 'Trade Validator', icon: ClipboardCheck, color: 'text-muted-foreground' },
        { path: '/plan', label: 'Trading Plan', icon: Shield, color: 'text-muted-foreground' },
        { path: '/risk-manager', label: 'Risk Manager', icon: ShieldAlert, color: 'text-muted-foreground' }
      ]
    },
    {
      label: 'Insights',
      items: [
        { path: '/review', label: 'Weekly Review', icon: BarChart3, color: 'text-muted-foreground' },
        { path: '/performance', label: 'Signal Insights', icon: Target, color: 'text-muted-foreground' },
        { path: '/pattern-performance', label: 'Pattern History', icon: History, color: 'text-muted-foreground' },
        { path: '/automation-readiness', label: 'Automation Score', icon: Activity, color: 'text-muted-foreground' }
      ]
    },
    {
      label: 'Help',
      items: [
        { path: '/about', label: 'About', icon: Info, color: 'text-muted-foreground' }
      ]
    }
  ];

  // Admin-only nav group, appended at runtime so non-admin users never see it.
  if (user?.role === 'admin') {
    navGroups.push({
      label: 'Admin',
      items: [
        { path: '/admin', label: 'Operator Console', icon: Shield, color: 'text-yellow-400' },
      ],
    });
  }
  
  const NavItem = ({ item, collapsed }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 group relative ${
          isActive
            ? 'bg-primary/10 text-primary border-l-2 border-primary ml-[-1px]'
            : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
        }`}
        title={collapsed ? item.label : undefined}
      >
        <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-primary' : item.color}`} />
        {!collapsed && <span className="truncate">{item.label}</span>}
        {isActive && !collapsed && (
          <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        )}
      </Link>
    );
  };
  
  const SidebarContent = ({ collapsed = false, mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? 'pt-4' : ''}`}>
      {/* Logo */}
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} px-4 py-4 border-b border-white/5`}>
        <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center shadow-lg shadow-primary/20">
          <Layers className="w-5 h-5 text-white" />
        </div>
        {!collapsed && (
          <div>
            <h1 className="text-lg font-black tracking-tight text-foreground">Trading Pilot</h1>
            <p className="text-[10px] text-primary uppercase tracking-wider font-bold">360</p>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {navGroups.map((group, idx) => (
          <div key={idx}>
            {!collapsed && (
              <h3 className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
                {group.label}
              </h3>
            )}
            <div className="space-y-1">
              {group.items.map(item => (
                <NavItem key={item.path} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      
      {/* Status Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-white/5 space-y-3">
          <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20">
            <Activity className="w-4 h-4 text-success animate-pulse" />
            <span className="text-xs font-medium text-success">System Active</span>
          </div>
          
          {/* User Info & Logout */}
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5">
            <div className="flex items-center space-x-2 min-w-0">
              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-destructive transition-colors"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Collapsed Logout */}
      {collapsed && (
        <div className="p-3 border-t border-white/5">
          <button
            onClick={logout}
            className="w-full p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-destructive transition-colors flex items-center justify-center"
            title="Logout"
            data-testid="logout-btn-collapsed"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
  
  return (
    <div className="min-h-screen bg-[#0a0a0c] relative">
      {/* Subtle gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0c] via-[#0d0d10] to-[#0a0a0c] pointer-events-none" />
      <div className="fixed inset-0 opacity-[0.015] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      }} />
      
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen bg-[#111113]/95 backdrop-blur-xl border-r border-white/5 z-40 transition-all duration-300 ${
          sidebarCollapsed ? 'w-[72px]' : 'w-[260px]'
        }`}
      >
        <SidebarContent collapsed={sidebarCollapsed} />
        
        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-[#1a1a1d] border border-white/10 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[#222225] transition-colors"
          data-testid="sidebar-collapse-btn"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5" />
          )}
        </button>
      </aside>
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#111113]/95 backdrop-blur-xl border-b border-white/5 z-50 flex items-center justify-between px-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center">
            <Layers className="w-4 h-4 text-white" />
          </div>
          <div className="flex items-baseline space-x-1">
            <h1 className="text-base font-bold tracking-tight text-foreground">Trading Pilot</h1>
            <span className="text-xs font-bold text-primary">360</span>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-muted-foreground hover:text-foreground"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed top-14 left-0 bottom-0 w-[280px] bg-[#111113]/98 backdrop-blur-xl border-r border-white/5 z-50 transform transition-transform duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent mobile />
      </aside>
      
      {/* Main Content */}
      <main 
        className={`relative z-10 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'
        } pt-14 lg:pt-0`}
      >
        {children}
      </main>
    </div>
  );
}
